/* eslint-disable no-param-reassign */
import { StateStore } from '@bingads-webui/state-store';
import _ from 'underscore';

import { DismissMode, INotification, NotificationLevel, TargetComponent, ExtraOptions, IContext } from '../types';
import { Context } from './context';
import { NotificationDataSource } from './notification-data-source';
import { getNotificationDataSource } from './notification-data-source-factory';
import {
  PermanentNotificationDismissStore,
  SessionStorageNotificationDismissStore
} from './notification-dismiss-store';
import { shouldClear, isAvailable } from '../utils';

interface INotificationStateStore {
  bannerNotificationList: INotification[];
  nQNotificationList: INotification[];
  floatingBannerNotification?: INotification | null;
}

export class NotificationStore extends StateStore<INotificationStateStore> {
  context: IContext;
  sessionDismissStore: SessionStorageNotificationDismissStore;
  permanenetDimissStore: PermanentNotificationDismissStore;
  floatingBannerId: string = 'floatingBannerId';
  dataSource: NotificationDataSource | null;
  getNotifications: Function | null;
  notificationFilter: Function | null;

  constructor(config?: {
    context?: IContext,
    dataSource?: NotificationDataSource | null,
    getNotifications? : Function | null,
    notificationFilter?: Function | null;
  }) {
    const defaultState:INotificationStateStore = {
      bannerNotificationList: [],
      nQNotificationList: [],
      floatingBannerNotification: null,
    };
    super(defaultState);
    this.context = (config && config.context) || new Context();
    this.sessionDismissStore = new SessionStorageNotificationDismissStore(this.context);
    this.permanenetDimissStore = new PermanentNotificationDismissStore(this.context);
    this.dataSource = (config && config.dataSource) || null;
    this.getNotifications = (config && config.getNotifications) || null;
    this.notificationFilter = (config && config.notificationFilter) || null;
    this.context.subscribe(() => {
      this.refresh();
    });
  }

  async refresh(override: boolean = false) {
    if (!this.getNotifications) {
      return;
    }

    if (!this.dataSource) {
      this.dataSource = await getNotificationDataSource(this.context, this.getNotifications);
    }
    let campaignNotifications = await this.dataSource.fetchCampaignNotificationStatus();

    campaignNotifications = this.filterDismissedNotifications(campaignNotifications);

    if (this.notificationFilter) {
      campaignNotifications = this.notificationFilter(campaignNotifications);
    }

    campaignNotifications.forEach((notification) => {
      this.buildDimissAction(notification);
    });

    const { bannerNotificationList, nQNotificationList, floatingBannerNotification } = this.groupNotifications(campaignNotifications);

    // dispatch notifications here
    this.setState({
      bannerNotificationList: override? bannerNotificationList: this.mergeToLeft(this.state.bannerNotificationList, bannerNotificationList),
      nQNotificationList: override? nQNotificationList : this.mergeToLeft(this.state.nQNotificationList, nQNotificationList),
      floatingBannerNotification: floatingBannerNotification || this.state.floatingBannerNotification,
    });
  }

  // TODO: Need to add unit test
  mergeToLeft(leftList: INotification[], rightList: INotification[]) {
    const rightMap: any = {};
    let resultList = [];
    rightList.forEach((item) => {
      rightMap[item.id] = item;
    });

    // clear notification that scopeLevel is lower than current change scope
    resultList = leftList.filter((notification) => {
      if (!isAvailable(this.context.getCurrentScope(), notification.availableScopes)) {
        return false;
      }
      if (notification.scopeLevel !== undefined && shouldClear(notification.scopeLevel, this.context.getChangeScope())) {
        return false;
      }

      return true;
    });

    // Replace old one to new one if one notification exists in both new UI and old UI
    resultList = resultList.map((item) => {
      if (rightMap[item.id]) {
        const newNotification = rightMap[item.id];
        rightMap[item.id] = undefined;
        return newNotification;
      }
      return item;
    });

    // Add new notification in rightList to result list
    Object.keys(rightMap).forEach((key) => {
      if (rightMap[key]) {
        resultList.push(rightMap[key]);
      }
    });

    return _.assign([], _.compact(resultList));
  }

  groupNotifications(notificationList: INotification[]) {
    const bannerNotificationList: INotification[] = [];
    const nQNotificationList: INotification[] = [];
    let floatingBannerNotification;

    notificationList.forEach((notification) => {
      if (notification.target === TargetComponent.TopBanner) {
        bannerNotificationList.push(notification);
      } else if (notification.target === TargetComponent.UnifiedQueue) {
        nQNotificationList.push(notification);
      } else if (notification.target === TargetComponent.FloatingBanner) {
        floatingBannerNotification = notification;
      }
    });

    return { bannerNotificationList, nQNotificationList, floatingBannerNotification };
  }

  filterDismissedNotifications(notifications: INotification[]) {
    return notifications.filter(notification => notification.condition === true && this.shouldShowNotification(notification));
  }

  /**
   * Will be called by Unified Notification Queue
   */
  dismissNQAll() {
    this.state.nQNotificationList.forEach((notification) => {
      if (notification.dismissAction) {
        notification.dismissAction(notification);
      }
    });
    this.setState({
      nQNotificationList: [],
    });
  }

  buildDimissAction(notification: INotification) {
    if (!notification.dismissMode) {
      notification.dismissMode = DismissMode.session;
    }

    switch (notification.dismissMode) {
      case DismissMode.session:
        notification.dismissAction = () => {
          this.sessionDismissStore.dimissNotification(notification.id);
          this.deleteNotificationFromList(notification.id);
        };
        break;
      case DismissMode.custom:
        // eslint-disable-next-line no-case-declarations
        const originDismissAction = notification.dismissAction;
        notification.dismissAction = () => {
          this.deleteNotificationFromList(notification.id);
          if (originDismissAction) {
            originDismissAction(notification);
          }
        };
        break;
      case DismissMode.once:
        notification.dismissAction = () => {
          this.deleteNotificationFromList(notification.id);
        };
        break;
      case DismissMode.permanent:
        notification.dismissAction = () => {
          this.permanenetDimissStore.dimissNotification(notification.id);
          this.deleteNotificationFromList(notification.id);
        };
        break;
      case DismissMode.never:
        notification.dismissAction = () => {};
        break;
      default:
        break;
    }
  }

  deleteNotificationFromList(id: string) {
    const bannerNotificationList = this.state.bannerNotificationList.filter(n => n.id !== id);

    const nQNotificationList = this.state.nQNotificationList.filter(n => n.id !== id);

    this.setState({
      bannerNotificationList,
      nQNotificationList,
    });
  }

  shouldShowNotification(notification: INotification) {
    return (
      this.sessionDismissStore.shouldShowNotification(notification.id) &&
      this.permanenetDimissStore.shouldShowNotification(notification.id)
    );
  }

  addnQNotifications(notifications: INotification[]) {
    const filteredNotifications = this.filterDismissedNotifications(notifications);
    this.setState({
      nQNotificationList: _.union(this.state.nQNotificationList, filteredNotifications),
    });
  }

  // used by notification api to add banner notification
  add(notification: INotification) {
    if (notification.severity === NotificationLevel.Info || notification.severity === NotificationLevel.Confirmation) {
      this.setFloatingBannerNotification(notification.message, NotificationLevel.Info);
    } else {
      this.setFloatingBannerNotification(notification.message, NotificationLevel.Warning);
    }
  }

  // used by notification api
  setFloatingBannerNotification(message: string, severity: NotificationLevel.Warning | NotificationLevel.Info, extraOptions?: ExtraOptions) {
    const notification: INotification = {
      message,
      title: '',
      severity,
      id: this.floatingBannerId,
      target: TargetComponent.FloatingBanner,
      condition: true,
      extraOptions,
    };
    this.setState({
      floatingBannerNotification: notification,
    });

    return this.floatingBannerId;
  }

  // used by notification api to dismiss notification
  dismiss(id: string) {
    if (id === this.floatingBannerId) {
      this.setState({
        floatingBannerNotification: undefined,
      });
    } else {
      this.deleteNotificationFromList(id);
    }
  }

  newId(): string {
    return _.uniqueId('notification-');
  }
}

let store: NotificationStore | null = null;
export const getStoreInstance = (config?: { context: IContext }) => {
  if (store) {
    return store;
  }
  store = new NotificationStore(config);
  return store;
};
